import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  User,
  Phone,
  Mail,
  Stethoscope,
  FileText,
  ShieldCheck,
  HeartPulse,
  MapPin,
  ChevronRight,
  Sparkles,
  CheckCircle2,
  XCircle,
  Sun,
  Sunset,
  MoonStar,
  Clock3,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API_BASE = "https://api.elitecarephysiorehab.com/api/appointments";

export default function BookAppointment() {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    appointmentDate: "",
    preferredTime: "",
    service: "",
    therapist: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotData, setSlotData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const services = [
    "Physiotherapy",
    "Sports Injury Rehab",
    "Spine Rehabilitation",
    "Post Surgical Recovery",
    "Pain Management",
    "Manual Therapy",
  ];

  const today = new Date().toISOString().split("T")[0];

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "appointmentDate" ? { preferredTime: "" } : {}),
    }));

    setErrorMessage("");
  };

  useEffect(() => {
    const fetchSlots = async () => {
      if (!formData.appointmentDate) {
        setSlotData([]);
        return;
      }

      try {
        setLoadingSlots(true);
        setErrorMessage("");

        const response = await fetch(
          `${API_BASE}/slots?date=${formData.appointmentDate}`
        );
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || "Failed to fetch slots");
        }

        setSlotData(result.slots || []);
      } catch (error) {
        setErrorMessage(error.message);
        setSlotData([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [formData.appointmentDate]);

  const handleSlotSelect = (slot) => {
    if (slot.isBooked) return;

    setFormData((prev) => ({
      ...prev,
      preferredTime: slot.time,
    }));
  };

  const slotGroups = useMemo(() => {
    const groups = {
      Morning: [],
      Afternoon: [],
      Evening: [],
    };

    slotData.forEach((slot) => {
      const [time, period] = slot.time.split(" ");
      const hour = parseInt(time.split(":")[0], 10);

      if (period === "AM") {
        groups.Morning.push(slot);
      } else if (period === "PM" && hour < 5) {
        groups.Afternoon.push(slot);
      } else {
        groups.Evening.push(slot);
      }
    });

    return groups;
  }, [slotData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    if (formData.fullName.trim().length < 3) {
      setErrorMessage("Name must be at least 3 characters long");
      return;
    }

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      setErrorMessage("Please enter a valid 10-digit phone number");
      return;
    }

    try {
      const response = await fetch(API_BASE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to create appointment");
      }

      setSubmitted(true);

      setTimeout(() => {
        setSubmitted(false);
      }, 3000);

      setFormData({
        fullName: "",
        phone: "",
        email: "",
        appointmentDate: "",
        preferredTime: "",
        service: "",
        therapist: "",
        message: "",
      });

      setSlotData([]);
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  return (
    <>
      <Navbar />

      <style>{`
        * { box-sizing: border-box; }

        .appointment-page {
          min-height: 100vh;
          padding: 54px 20px 90px;
          font-family: Arial, sans-serif;
          background:
            radial-gradient(circle at top left, rgba(34, 197, 94, 0.12), transparent 24%),
            radial-gradient(circle at bottom right, rgba(21, 128, 61, 0.10), transparent 22%),
            linear-gradient(180deg, #f7fff8 0%, #ffffff 46%, #f2fbf4 100%);
        }

        .appointment-wrapper {
          max-width: 1320px;
          margin: 0 auto;
        }

        .appointment-top {
          text-align: center;
          margin-bottom: 32px;
        }

        .appointment-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(34, 197, 94, 0.10);
          color: #15803d;
          border: 1px solid rgba(34, 197, 94, 0.18);
          padding: 10px 18px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 800;
          margin-bottom: 18px;
        }

        .appointment-top h1 {
          font-size: 50px;
          line-height: 1.1;
          color: #0f172a;
          margin-bottom: 14px;
          font-weight: 900;
        }

        .appointment-top h1 span { color: #16a34a; }

        .appointment-top p {
          max-width: 780px;
          margin: 0 auto;
          color: #64748b;
          font-size: 17px;
          line-height: 1.85;
        }

        .appointment-grid {
          display: grid;
          grid-template-columns: 390px 1fr;
          gap: 28px;
          align-items: start;
        }

        .appointment-left {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .appointment-hero-card {
          background: linear-gradient(155deg, #15803d 0%, #16a34a 48%, #22c55e 100%);
          border-radius: 32px;
          padding: 30px 26px;
          color: #fff;
          box-shadow: 0 24px 55px rgba(21, 128, 61, 0.20);
          position: sticky;
          top: 118px;
        }

        .mini-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-radius: 999px;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.15);
          font-size: 12px;
          font-weight: 800;
          margin-bottom: 18px;
        }

        .appointment-hero-card h2 {
          font-size: 32px;
          line-height: 1.18;
          margin-bottom: 12px;
          font-weight: 900;
        }

        .appointment-hero-card p {
          color: rgba(255,255,255,0.92);
          line-height: 1.8;
          font-size: 14px;
          margin-bottom: 22px;
        }

        .appointment-benefits {
          display: grid;
          gap: 14px;
          margin-bottom: 22px;
        }

        .appointment-benefit {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 18px;
          padding: 14px;
        }

        .appointment-benefit-icon {
          width: 42px;
          height: 42px;
          min-width: 42px;
          border-radius: 12px;
          background: rgba(255,255,255,0.16);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .appointment-benefit h4 {
          font-size: 14px;
          font-weight: 800;
          margin-bottom: 4px;
        }

        .appointment-benefit p {
          font-size: 12px;
          margin: 0;
          line-height: 1.7;
          color: rgba(255,255,255,0.88);
        }

        .appointment-help-box {
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.14);
          border-radius: 20px;
          padding: 16px;
        }

        .appointment-help-box h5 {
          font-size: 15px;
          margin-bottom: 12px;
          font-weight: 800;
        }

        .appointment-help-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          color: rgba(255,255,255,0.94);
          margin-bottom: 10px;
        }

        .appointment-help-item:last-child { margin-bottom: 0; }

        .appointment-form-card {
          background: rgba(255,255,255,0.90);
          border: 1px solid rgba(15, 23, 42, 0.05);
          box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
          border-radius: 32px;
          padding: 32px;
        }

        .appointment-form-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 20px;
          padding-bottom: 22px;
          margin-bottom: 24px;
          border-bottom: 1px solid #eef2f7;
        }

        .appointment-form-head h3 {
          font-size: 30px;
          font-weight: 900;
          color: #0f172a;
          margin-bottom: 8px;
        }

        .appointment-form-head p {
          color: #64748b;
          font-size: 15px;
          line-height: 1.8;
        }

        .appointment-quick-tag {
          background: linear-gradient(135deg, #ecfdf3, #f6fff8);
          color: #15803d;
          border: 1px solid #d9fbe6;
          border-radius: 18px;
          padding: 12px 16px;
          font-size: 13px;
          font-weight: 800;
          white-space: nowrap;
        }

        .appointment-form {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-label {
          font-size: 14px;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 9px;
        }

        .input-wrap {
          position: relative;
        }

        .input-icon {
          position: absolute;
          top: 50%;
          left: 14px;
          transform: translateY(-50%);
          color: #16a34a;
          width: 18px;
          height: 18px;
        }

        .appointment-input,
        .appointment-textarea {
          width: 100%;
          border: 1px solid #dbe5dd;
          border-radius: 18px;
          padding: 15px 16px 15px 46px;
          font-size: 15px;
          color: #0f172a;
          background: #f8fbf8;
          outline: none;
        }

        .appointment-textarea {
          min-height: 150px;
          resize: vertical;
          padding-top: 16px;
        }

        .textarea-icon {
          top: 18px !important;
          transform: none !important;
        }

        .service-section {
          grid-column: 1 / -1;
        }

        .service-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .service-card {
          border: 1px solid #dbe5dd;
          background: #f8fbf8;
          border-radius: 18px;
          padding: 16px;
          cursor: pointer;
          transition: 0.25s ease;
        }

        .service-card:hover {
          transform: translateY(-2px);
          background: #fff;
          border-color: #22c55e;
        }

        .service-card.active {
          border-color: #16a34a;
          background: linear-gradient(135deg, #effcf3, #f7fff9);
        }

        .service-card h5 {
          margin: 0 0 6px;
          font-size: 14px;
          color: #0f172a;
          font-weight: 800;
        }

        .service-card p {
          margin: 0;
          font-size: 12px;
          color: #64748b;
          line-height: 1.6;
        }

        .slots-section {
          grid-column: 1 / -1;
        }

        .slots-header h4 {
          margin: 0 0 6px;
          font-size: 17px;
          font-weight: 900;
          color: #0f172a;
        }

        .slots-header p {
          margin: 0 0 14px;
          color: #64748b;
          font-size: 13px;
          line-height: 1.7;
        }

        .slot-group {
          margin-bottom: 18px;
        }

        .slot-group-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          color: #0f172a;
          font-size: 14px;
          font-weight: 800;
        }

        .slots-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .slot-btn {
          border: 1px solid #dbe5dd;
          background: #f8fbf8;
          border-radius: 18px;
          padding: 14px 12px;
          text-align: center;
          cursor: pointer;
          transition: 0.25s ease;
          font-size: 14px;
          font-weight: 800;
          color: #0f172a;
          min-height: 66px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 4px;
        }

        .slot-btn:hover {
          transform: translateY(-2px);
          border-color: #22c55e;
          background: #fff;
        }

        .slot-btn.active {
          background: linear-gradient(135deg, #15803d 0%, #16a34a 45%, #22c55e 100%);
          color: #fff;
          border-color: #16a34a;
        }

        .slot-btn.filled {
          background: #f8fafc;
          color: #94a3b8;
          border-color: #e2e8f0;
          cursor: not-allowed;
        }

        .slot-status {
          font-size: 11px;
          font-weight: 800;
        }

        .slot-status.available {
          color: #15803d;
          opacity: 0.85;
        }

        .slot-status.filled {
          color: #ef4444;
        }

        .no-date-box, .loading-box, .error-box {
          border-radius: 22px;
          padding: 18px;
          font-size: 14px;
          line-height: 1.8;
        }

        .no-date-box {
          border: 1px dashed #cfe9d6;
          background: linear-gradient(135deg, #fbfefc, #f4fbf6);
          color: #64748b;
        }

        .loading-box {
          border: 1px solid #dff3e4;
          background: #f7fdf8;
          color: #15803d;
        }

        .error-box {
          border: 1px solid #fecaca;
          background: #fef2f2;
          color: #b91c1c;
          grid-column: 1 / -1;
        }

        .selected-summary-card {
          grid-column: 1 / -1;
          background: linear-gradient(135deg, #f8fdf9, #edf9f0);
          border: 1px solid #dff3e4;
          border-radius: 24px;
          padding: 18px;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }

        .selected-chip {
          background: rgba(255,255,255,0.75);
          border: 1px solid #e7f2ea;
          border-radius: 16px;
          padding: 14px;
        }

        .selected-chip-label {
          font-size: 11px;
          color: #64748b;
          font-weight: 800;
          margin-bottom: 6px;
          text-transform: uppercase;
        }

        .selected-chip-value {
          font-size: 14px;
          color: #0f172a;
          font-weight: 800;
          line-height: 1.6;
        }

        .appointment-summary {
          grid-column: 1 / -1;
          display: grid;
          grid-template-columns: 1.2fr auto;
          gap: 18px;
          align-items: center;
          margin-top: 8px;
          padding: 20px;
          border-radius: 24px;
          background: linear-gradient(135deg, #f8fdf9, #edf9f0);
          border: 1px solid #dff3e4;
        }

        .appointment-summary-left {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .appointment-summary-left span {
          font-size: 14px;
          line-height: 1.8;
          color: #475569;
        }

        .book-btn-submit {
          border: none;
          background: linear-gradient(135deg, #15803d 0%, #16a34a 45%, #22c55e 100%);
          color: #fff;
          padding: 16px 30px;
          border-radius: 18px;
          font-size: 16px;
          font-weight: 900;
          cursor: pointer;
          min-width: 230px;
        }

        .book-btn-submit:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }

        .success-modal {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          z-index: 9999;
        }

        .success-card {
          width: 100%;
          max-width: 420px;
          background: rgba(255,255,255,0.96);
          border-radius: 28px;
          padding: 28px 24px;
          box-shadow: 0 24px 60px rgba(15, 23, 42, 0.16);
          text-align: center;
        }

        .success-icon {
          width: 68px;
          height: 68px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          background: linear-gradient(135deg, #ecfdf3, #dcfce7);
          color: #16a34a;
        }

        .success-card h4 {
          margin: 0 0 8px;
          font-size: 24px;
          color: #15803d;
          font-weight: 900;
        }

        .success-card p {
          margin: 0;
          font-size: 14px;
          color: #64748b;
          line-height: 1.85;
        }

        @media (max-width: 1150px) {
          .appointment-grid {
            grid-template-columns: 1fr;
          }
          .appointment-hero-card {
            position: relative;
            top: 0;
          }
        }

        @media (max-width: 1000px) {
          .service-grid,
          .slots-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          .selected-summary-card {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 900px) {
          .appointment-top h1 {
            font-size: 40px;
          }
          .appointment-form-head {
            flex-direction: column;
            align-items: flex-start;
          }
        }

        @media (max-width: 768px) {
          .appointment-page {
            padding: 42px 14px 68px;
          }
          .appointment-top h1 {
            font-size: 32px;
          }
          .appointment-top p {
            font-size: 15px;
          }
          .appointment-hero-card,
          .appointment-form-card {
            padding: 24px 18px;
            border-radius: 24px;
          }
          .appointment-hero-card h2 {
            font-size: 28px;
          }
          .appointment-form-head h3 {
            font-size: 26px;
          }
          .appointment-form {
            grid-template-columns: 1fr;
          }
          .slots-grid,
          .service-grid {
            grid-template-columns: 1fr;
          }
          .appointment-summary {
            grid-template-columns: 1fr;
          }
          .book-btn-submit {
            width: 100%;
          }
        }
      `}</style>

      {submitted && (
        <div className="success-modal">
          <div className="success-card">
            <div className="success-icon">
              <CheckCircle2 size={32} />
            </div>
            <h4>Appointment Confirmed</h4>
            <p>
              Your appointment request has been submitted successfully. Our team
              will review it and contact you shortly with confirmation details.
            </p>
          </div>
        </div>
      )}

      <section className="appointment-page">
        <div className="appointment-wrapper">
          <div className="appointment-top">
            <div className="appointment-badge">
              <Sparkles size={15} />
              BOOK YOUR CONSULTATION
            </div>

            <h1>
              Begin Your <span>Healing Journey</span> Today
            </h1>

            <p>
              Select your date, choose your service, view available 30-minute
              slots, and book your appointment with confidence.
            </p>
          </div>

          <div className="appointment-grid">
            <div className="appointment-left">
              <div className="appointment-hero-card">
                <div className="appointment-hero-inner">
                  <div className="mini-tag">
                    <HeartPulse size={14} />
                    ELITE CARE APPOINTMENT DESK
                  </div>

                  <h2>Smart Slot Booking. Better Patient Experience.</h2>

                  <p>
                    Pick a date, select a service, and book only from the slots
                    that are truly available for that day.
                  </p>

                  <div className="appointment-benefits">
                    <div className="appointment-benefit">
                      <div className="appointment-benefit-icon">
                        <HeartPulse size={20} />
                      </div>
                      <div>
                        <h4>Date-Wise Slot Control</h4>
                        <p>Only free time slots remain available for new bookings.</p>
                      </div>
                    </div>

                    <div className="appointment-benefit">
                      <div className="appointment-benefit-icon">
                        <Clock3 size={20} />
                      </div>
                      <div>
                        <h4>30-Minute Structured Slots</h4>
                        <p>Time is neatly managed in clinic-friendly intervals.</p>
                      </div>
                    </div>

                    <div className="appointment-benefit">
                      <div className="appointment-benefit-icon">
                        <ShieldCheck size={20} />
                      </div>
                      <div>
                        <h4>No Double Booking</h4>
                        <p>Already booked slots are shown as filled and locked.</p>
                      </div>
                    </div>
                  </div>

                  <div className="appointment-help-box">
                    <h5>Need Help Before Booking?</h5>

                    <div className="appointment-help-item">
                      <Phone size={16} />
                      <span>+91 9182643439</span>
                    </div>

                    <div className="appointment-help-item">
                      <Mail size={16} />
                      <span>support@elitecare.com</span>
                    </div>

                    <div className="appointment-help-item">
                      <MapPin size={16} />
                      <span>Fast response and booking assistance available</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="appointment-right">
              <div className="appointment-form-card">
                <div className="appointment-form-head">
                  <div>
                    <h3>Book an Appointment</h3>
                    <p>
                      Fill your details, select a service, then choose from the
                      available slots for your selected date.
                    </p>
                  </div>

                  <div className="appointment-quick-tag">
                    Premium Booking Flow
                  </div>
                </div>

                <form className="appointment-form" onSubmit={handleSubmit}>
                  {errorMessage && (
                    <div className="error-box">
                      {errorMessage}
                    </div>
                  )}

                  <div className="form-group">
                    <label className="form-label">Full Name <span style={{ color: '#ef4444' }}>*</span></label>
                    <div className="input-wrap">
                      <User className="input-icon" />
                      <input
                        type="text"
                        name="fullName"
                        placeholder="Enter your full name"
                        className="appointment-input"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        minLength={3}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                    <div className="input-wrap">
                      <Phone className="input-icon" />
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Enter your phone number"
                        className="appointment-input"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        pattern="[0-9]{10}"
                        title="Please enter a valid 10-digit phone number"
                      />
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label className="form-label">Email Address <span style={{ fontWeight: 400 }}>(Optional)</span></label>
                    <div className="input-wrap">
                      <Mail className="input-icon" />
                      <input
                        type="email"
                        name="email"
                        placeholder="Enter your email address"
                        className="appointment-input"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group full-width service-section">
                    <label className="form-label">Choose Service <span style={{ fontWeight: 400 }}>(Optional)</span></label>
                    <div className="service-grid">
                      {services.map((service) => (
                        <div
                          key={service}
                          className={`service-card ${
                            formData.service === service ? "active" : ""
                          }`}
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              service,
                            }))
                          }
                        >
                          <h5>{service}</h5>
                          <p>Professional care and structured treatment support</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Appointment Date <span style={{ fontWeight: 400 }}>(Optional)</span></label>
                    <div className="input-wrap">
                      <CalendarDays className="input-icon" />
                      <input
                        type="date"
                        name="appointmentDate"
                        min={today}
                        className="appointment-input"
                        value={formData.appointmentDate}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Preferred Doctor / Therapist <span style={{ fontWeight: 400 }}>(Optional)</span></label>
                    <div className="input-wrap">
                      <Stethoscope className="input-icon" />
                      <input
                        type="text"
                        name="therapist"
                        placeholder="Enter preferred doctor or therapist name"
                        className="appointment-input"
                        value={formData.therapist}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="slots-section">
                    <div className="slots-header">
                      <div>
                        <h4>Available 30-Minute Time Slots</h4>
                        <p>
                          {formData.appointmentDate
                            ? `Showing slots for ${formData.appointmentDate}`
                            : "Please select a date first to view available slots."}
                        </p>
                      </div>
                    </div>

                    {loadingSlots ? (
                      <div className="loading-box">Loading available slots...</div>
                    ) : formData.appointmentDate ? (
                      <>
                        {Object.entries(slotGroups).map(([label, slots]) => {
                          if (!slots.length) return null;

                          const icon =
                            label === "Morning" ? (
                              <Sun size={16} />
                            ) : label === "Afternoon" ? (
                              <Sunset size={16} />
                            ) : (
                              <MoonStar size={16} />
                            );

                          return (
                            <div className="slot-group" key={label}>
                              <div className="slot-group-header">
                                {icon}
                                <span>{label}</span>
                              </div>

                              <div className="slots-grid">
                                {slots.map((slot) => {
                                  const isFilled = slot.isBooked;
                                  const isActive =
                                    formData.preferredTime === slot.time;

                                  return (
                                    <button
                                      key={slot.time}
                                      type="button"
                                      className={`slot-btn ${
                                        isFilled ? "filled" : ""
                                      } ${isActive ? "active" : ""}`}
                                      onClick={() => handleSlotSelect(slot)}
                                      disabled={isFilled}
                                    >
                                      <span>{slot.time}</span>
                                      <span
                                        className={`slot-status ${
                                          isFilled ? "filled" : "available"
                                        }`}
                                      >
                                        {isFilled ? "Filled" : "Available"}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <div className="no-date-box">
                        Select the appointment date first. After that, you will
                        see all 30-minute slots grouped into morning, afternoon,
                        and evening.
                      </div>
                    )}
                  </div>

                  <div className="form-group full-width">
                    <label className="form-label">Symptoms / Message <span style={{ fontWeight: 400 }}>(Optional)</span></label>
                    <div className="input-wrap">
                      <FileText className="input-icon textarea-icon" />
                      <textarea
                        name="message"
                        placeholder="Describe your symptoms, pain area, injury, or recovery concern"
                        className="appointment-textarea"
                        value={formData.message}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="selected-summary-card">
                    <div className="selected-chip">
                      <div className="selected-chip-label">Selected Service</div>
                      <div className="selected-chip-value">
                        {formData.service || "Not selected"}
                      </div>
                    </div>

                    <div className="selected-chip">
                      <div className="selected-chip-label">Selected Date</div>
                      <div className="selected-chip-value">
                        {formData.appointmentDate || "Not selected"}
                      </div>
                    </div>

                    <div className="selected-chip">
                      <div className="selected-chip-label">Selected Slot</div>
                      <div className="selected-chip-value">
                        {formData.preferredTime || "Not selected"}
                      </div>
                    </div>
                  </div>

                  <div className="appointment-summary">
                    <div className="appointment-summary-left">
                      {formData.preferredTime ? (
                        <CheckCircle2 size={20} color="#16a34a" />
                      ) : (
                        <XCircle size={20} color="#ef4444" />
                      )}

                      <span>
                        {formData.preferredTime
                          ? `You selected ${formData.preferredTime} on ${formData.appointmentDate}. This slot will be blocked for others after booking.`
                          : "You can proceed to confirm your appointment. Selecting a date and time is optional."}
                      </span>
                    </div>

                    <button
                      type="submit"
                      className="book-btn-submit"
                    >
                      Confirm Appointment
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}